import { forwardRef, memo, useCallback, useMemo, useRef } from 'react';
import { NumericFormat } from 'react-number-format';
import { getSystemNumberSeparators } from 'utils/numberLocale';

type NumberFormatRandsProps = {
  onChange: (event: { target: { name: string; value: string } }) => void;
  value: number | string;
  name: string;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
};

function setRef(ref: React.Ref<HTMLInputElement> | undefined, el: HTMLInputElement | null) {
  if (typeof ref === 'function') ref(el);
  else if (ref) (ref as { current: HTMLInputElement | null }).current = el;
}

const NumberFormatRands = memo(
  forwardRef<HTMLInputElement, NumberFormatRandsProps>(function NumberFormatRands(
    { onChange, value, name, onFocus, ...rest },
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const locale = useMemo(getSystemNumberSeparators, []);

    const cents = typeof value === 'string' ? Number(value) || 0 : Number(value) || 0;
    const isZero = cents === 0;

    const getInputRef = useCallback(
      (el: HTMLInputElement | null) => {
        inputRef.current = el;
        setRef(ref, el);
      },
      [ref],
    );

    const moveCursorToEnd = useCallback(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            const el = inputRef.current;
            if (el) {
              el.focus();
              el.setSelectionRange(el.value.length, el.value.length);
            }
          }, 0);
        });
      });
    }, []);

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (isZero) moveCursorToEnd();
        onFocus?.(e);
      },
      [isZero, moveCursorToEnd, onFocus],
    );

    const handleValueChange = useCallback(
      (values: { floatValue?: number }) => {
        const centsValue = Math.round((values.floatValue ?? 0) * 100);
        if (centsValue === 0) moveCursorToEnd();
        onChange({ target: { name, value: String(centsValue) } });
      },
      [name, onChange, moveCursorToEnd],
    );

    const displayValue = cents / 100;

    return (
      <NumericFormat
        {...rest}
        value={displayValue === 0 ? '0' : String(displayValue)}
        getInputRef={getInputRef}
        onFocus={handleFocus}
        onValueChange={handleValueChange}
        inputMode="decimal"
        pattern="[0-9]*"
        decimalScale={2}
        decimalSeparator={locale.decimalSeparator}
        thousandSeparator={locale.thousandSeparator}
        valueIsNumericString
        prefix="R"
      />
    );
  }),
);

NumberFormatRands.displayName = 'NumberFormatRands';

export default NumberFormatRands;
